import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../hooks';

const About = () => {
  const { t } = useTranslation();
  const [currentPersonnel, setCurrentPersonnel] = useState(0);

  const teamImages = ['/images/nyantsa.jpeg','/images/vev.jpeg', '/images/clement.jpg'];

  const statsData = [
    { number: '10+', label: t.stats.projects },
    { number: '2+', label: t.stats.experience },
    { number: '10+', label: t.stats.clients },
    { number: 'Madagascar', label: t.stats.locations }
  ];

  const nextPersonnel = () => {
    setCurrentPersonnel((prev) => (prev + 1) % t.team.length);
  };

  const prevPersonnel = () => {
    setCurrentPersonnel((prev) => (prev - 1 + t.team.length) % t.team.length);
  };

  return (
    <section id="about" className="py-24 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl lg:text-5xl font-light mb-8 tracking-wide">{t.about.title}</h2>
            <div className="space-y-6 text-gray-300 leading-relaxed">
              {t.about.paragraphs.map((paragraph, index) => (
                <p key={index} className={index === 0 ? 'text-xl' : ''}>
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-8 mt-12">
              {statsData.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-light text-white mb-3">{stat.number}</div>
                  <div className="text-sm text-gray-400 uppercase tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Personnel Carousel */}
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentPersonnel * 100}%)` }}
              >
                {t.team.map((person, index) => (
                  <div key={person.id} className="w-full flex-shrink-0">
                    <div className="aspect-square bg-gray-800 overflow-hidden">
                      <img
                        src={teamImages[index]}
                        alt={`${person.name} - M.A CONCEPT`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="bg-black/70 text-white p-4 text-left">
                      <h4 className="text-lg font-light tracking-wider">{person.name}</h4>
                      <div className="relative group">
                        <p className="text-sm font-light tracking-wide text-gray-300 cursor-pointer">
                          {person.profession}
                        </p>
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-white text-black px-4 py-2 rounded shadow-lg whitespace-nowrap z-10">
                          <p className="text-sm font-medium">{person.specialization}</p>
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              {t.team.length > 1 && (
                <>
                  <button
                    onClick={prevPersonnel}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white p-2 rounded-full transition-all"
                    aria-label="Previous member"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextPersonnel}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white p-2 rounded-full transition-all"
                    aria-label="Next member"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              {/* Dots Indicator */}
              {t.team.length > 1 && (
                <div
              className="relative z-30 flex justify-center mt-4 space-x-2"
              style={{ transform: 'translateX(-40px)' }}
            >
              {t.team.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPersonnel(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentPersonnel === index ? 'bg-white' : 'bg-white/50'
                  }`}
                  aria-label={`View member ${index + 1}`}
                />
              ))}
            </div>
              )}
            </div>

            {/* Mission Box */}
            <div className="absolute -bottom-8 bg-white text-black p-8 max-w-xs" style={{ right: '-44px' }}>
              <h3 className="font-semibold mb-2 tracking-wide">{t.about.mission.title}</h3>
              <p className="text-sm leading-relaxed">
                {t.about.mission.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
